// src/components/Modals/DomainPurchaseModal/DomainPurchaseModal.tsx

import { CustomDomainService } from '@/services/CustomDomainService';
import { useCustomDomainsStore } from '@/stores/useCustomDomainsStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { ensureHttps } from '@/utils/LinkUtility';
import { isTrialAccount } from '@/utils/CommonUtility';
import {
    faExclamationCircle,
    faLightbulb,
    faSearch,
    faGlobe,
    faCrown,
    faCheck,
    faTimes,
    faExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Modal,
    TextInput,
    Title,
    Text,
    Button,
    Alert,
    Flex,
    Box,
    Group,
    Loader,
    Divider,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
    keyword: z.string().min(1, 'Domain keyword is required'),
});

const purchaseSchema = z.object({
    domain_name: z.string().min(1, 'Domain name is required'),
    years: z.number().min(1).max(10),
    redirect_404_url: z.string().url().optional().nullable(),
    branded_domain_url: z.string().url().optional().nullable(),
});

export const DomainPurchaseModal = ({
    opened,
    setOpened,
    plan
}: {
    opened: boolean;
    setOpened: (opened: boolean) => void;
    plan: any;
}) => {
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [mainDomain, setMainDomain] = useState<any>(null);
    const [alternativeDomains, setAlternativeDomains] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [purchasingDomainId, setPurchasingDomainId] = useState<string | null>(null);

    const [customDomains, setCustomDomains] = useCustomDomainsStore((state) => [
        state.customDomains,
        state.setCustomDomains,
    ]);

    const [defaultWorkspace] = useWorkspaceStore((state) => [state.defaultWorkspace]);

    const searchForm = useForm({
        initialValues: {
            keyword: '',
        },
        validate: zodResolver(searchSchema),
    });

    // Static contact information to use for all domain purchases
    const staticContactInfo = {
        firstName: 'waqar',
        lastName: 'azeem',
        address1: 'D4 Interactive, Plaza 70, Civic Center Bahria Town Phase 4, Bahria Town, Rawalpindi',
        city: 'Rawalpindi',
        state: 'Punjab',
        zip: '46220',
        country: 'Pakistan',
        phone: '+923215137323',
        email: 'admin@replug.io',
    };

    const handleDomainSearch = async (values: any) => {
        setIsSearching(true);
        setMainDomain(null);
        setAlternativeDomains([]);
        try {
            const response = await new CustomDomainService().searchDomains(
                values.keyword + '.link',
                ['link']
            );

            console.log('Domain search response:', response);
            console.log('Search results structure:', response.data);

            // Check if we have a valid response with results
            if (response.data && response.data.status && response.data.data && response.data.data.data && response.data.data.data.results) {
                // Make sure we're accessing the correct path to the results array
                const results = response.data.data.data.results || [];
                console.log('Extracted results:', results);

                // Only set the main domain (first result that matches exactly what was searched)
                const exactMatch = results.find((domain: any) =>
                    domain.domainName.toLowerCase() === (values.keyword + '.link').toLowerCase()
                );

                if (exactMatch) {
                    setMainDomain(exactMatch);
                    
                    // If the domain is not available, find alternatives
                    if (!exactMatch.purchasable && results.length > 1) {
                        // Filter out the main domain and get up to 3 alternatives that are purchasable
                        const alternatives = results
                            .filter((domain: any) => 
                                domain.domainName.toLowerCase() !== exactMatch.domainName.toLowerCase() && 
                                domain.purchasable
                            )
                            .slice(0, 3);
                        
                        setAlternativeDomains(alternatives);
                    }
                } else if (results.length > 0) {
                    // If no exact match, use the first result
                    setMainDomain(results[0]);
                    
                    // Get up to 3 alternatives (excluding the main one)
                    if (results.length > 1) {
                        setAlternativeDomains(results.slice(1, 4).filter((domain: any) => domain.purchasable));
                    }
                }

                if (results.length === 0) {
                    notifications.show({
                        color: 'blue',
                        message: 'No domains found matching your search criteria.',
                    });
                }
            } else {
                notifications.show({
                    color: 'red',
                    message: 'Error searching domains: ' + (response.data?.message || 'Unknown error'),
                });
            }
        } catch (error) {
            console.error('Error searching domains:', error);
            notifications.show({
                color: 'red',
                message: 'Error searching domains. Please try again.',
            });
        } finally {
            setIsSearching(false);
        }
    };

    const handleClaimDomain = async () => {
        if (!mainDomain || !mainDomain.purchasable) return;

        setIsPurchasing(true);
        setPurchasingDomainId(mainDomain.domainName);
        try {
            // Prepare domain purchase data with static contact info
            const purchaseData = {
                domain_name: mainDomain.domainName,
                years: 1,
                redirect_404_url: null,
                branded_domain_url: null,
                contact_info: staticContactInfo
            };

            const response = await new CustomDomainService().purchaseDomain(
                defaultWorkspace?._id || '',
                purchaseData as any
            );

            if (response.data.status) {
                setOpened(false);
                setCustomDomains([...customDomains, response.data.data]);
                notifications.show({
                    color: 'green',
                    message: 'Domain purchased successfully! DNS records have been configured, but it may take up to 24 hours for changes to propagate.',
                });
            } else {
                notifications.show({
                    color: 'red',
                    message: 'Error purchasing domain: ' + (response.data.message || 'Unknown error'),
                });
            }
        } catch (error: any) {
            console.error('Error purchasing domain:', error);
            notifications.show({
                color: 'red',
                message: 'Error purchasing domain: ' + (error.response?.data?.message || 'Please try again.'),
            });
        } finally {
            setIsPurchasing(false);
            setPurchasingDomainId(null);
        }
    };

    const handleDirectPurchase = async (domain: any) => {
        if (!domain || !domain.purchasable) return;

        setIsPurchasing(true);
        setPurchasingDomainId(domain.domainName);
        try {
            // Prepare domain purchase data with static contact info
            const purchaseData = {
                domain_name: domain.domainName,
                years: 1,
                redirect_404_url: null,
                branded_domain_url: null,
                contact_info: staticContactInfo
            };

            const response = await new CustomDomainService().purchaseDomain(
                defaultWorkspace?._id || '',
                purchaseData as any
            );

            if (response.data.status) {
                setOpened(false);
                setCustomDomains([...customDomains, response.data.data]);
                notifications.show({
                    color: 'green',
                    message: 'Domain purchased successfully! DNS records have been configured, but it may take up to 24 hours for changes to propagate.',
                });
            } else {
                notifications.show({
                    color: 'red',
                    message: 'Error purchasing domain: ' + (response.data.message || 'Unknown error'),
                });
            }
        } catch (error: any) {
            console.error('Error purchasing domain:', error);
            notifications.show({
                color: 'red',
                message: 'Error purchasing domain: ' + (error.response?.data?.message || 'Please try again.'),
            });
        } finally {
            setIsPurchasing(false);
            setPurchasingDomainId(null);
        }
    };

    const handleClose = () => {
        setOpened(false);
        searchForm.reset();
        setMainDomain(null);
        setSearchResults([]);
    };

    return (
        <>
            <Modal
                opened={opened}
                onClose={handleClose}
                title={<Title order={4}>Claim your .link domain</Title>}
                size={700}
            >
                <Alert color="blue" icon={<FontAwesomeIcon icon={faCrown} />} mb={16}>
                    <Text size="sm" mt={2}>
                        <strong> Premium feature: </strong> .link domains available exclusively for paid plans.
                    </Text>
                </Alert>

                <Box mb={24}>
                    <Text fw={500} size="sm" mb={8}>Search Domains</Text>
                    <form onSubmit={searchForm.onSubmit(handleDomainSearch)}>
                        <Flex gap="md" align="flex-end">
                            <Box style={{ flex: 1, position: 'relative' }}>
                                <TextInput
                                    placeholder="Enter a domain name"
                                    required
                                    style={{ width: '100%' }}
                                    {...searchForm.getInputProps('keyword')}
                                    rightSection={
                                        <Text c="dimmed" fw={500} style={{ userSelect: 'none' }}>
                                            .link
                                        </Text>
                                    }
                                    rightSectionWidth={50}
                                />
                            </Box>
                            <Button
                                type="submit"
                                loading={isSearching}
                                leftSection={<FontAwesomeIcon icon={faSearch} />}
                            >
                                Search
                            </Button>
                        </Flex>
                    </form>
                </Box>

                {mainDomain && (
                    <>
                        <Box mb={24}>
                            <Box
                                style={{
                                    border: mainDomain.purchasable ? '1px solid #d4edda' : '1px solid #f8d7da',
                                    borderRadius: '8px',
                                    backgroundColor: mainDomain.purchasable ? '#e8f5e9' : '#fff3f3',
                                    padding: '16px',
                                }}
                            >
                                <Flex align="center" gap="sm">
                                    {mainDomain.purchasable && (
                                        <Box
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                backgroundColor: '#37b24d',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faCheck} color="#fff" size="xs" />
                                        </Box>
                                    )}
                                    {!mainDomain.purchasable && (
                                        <Box
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                backgroundColor: '#f59f00',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faExclamation} color="#fff" size="xs" />
                                        </Box>
                                    )}
                                    <Box>
                                        <Text fw={600} size="md" c="dark.8">
                                            {mainDomain.domainName}
                                        </Text>
                                        <Text size="sm" c={mainDomain.purchasable ? "green.7" : "yellow.9"}>
                                            {mainDomain.purchasable 
                                                ? 'is available. Claim your free domain before it\'s gone!' 
                                                : 'is not available. Check out our suggestions below.'}
                                        </Text>
                                    </Box>
                                </Flex>
                            </Box>
                        </Box>

                        {/* Show alternative domains if main domain is not available */}
                        {!mainDomain.purchasable && alternativeDomains.length > 0 && (
                            <Box mb={24}>
                                <Text fw={500} size="sm" mb={8}>Available alternatives</Text>
                                {alternativeDomains.map((domain, index) => (
                                    <Flex 
                                        key={index} 
                                        justify="space-between" 
                                        align="center" 
                                        mb={8}
                                        style={{
                                            padding: '8px 12px',
                                            borderBottom: index < alternativeDomains.length - 1 ? '1px solid #f0f0f0' : 'none'
                                        }}
                                    >
                                        <Flex align="center" gap="sm">
                                            <Box
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#4CAF50',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faCheck} color="#fff" size="xs" />
                                            </Box>
                                            <Text>{domain.domainName}</Text>
                                        </Flex>
                                        <Button 
                                            variant="outline" 
                                            size="xs"
                                            onClick={() => {
                                                if (isTrialAccount(plan)) {
                                                    window.open('/account/billing', '_blank');
                                                } else {
                                                    handleDirectPurchase(domain);
                                                }
                                            }}
                                            loading={isPurchasing && purchasingDomainId === domain.domainName}
                                            disabled={(isPurchasing && purchasingDomainId !== domain.domainName)}
                                            title={isTrialAccount(plan) ? "Upgrade to a paid plan to claim this domain" : ""}
                                        >
                                            {isTrialAccount(plan) ? "Upgrade Plan" : "Claim domain"}
                                        </Button>
                                    </Flex>
                                ))}
                            </Box>
                        )}

                        <Text size="xs" c="dimmed" mb={24} style={{ textAlign: 'center' }}>
                            {isTrialAccount(plan)
                                ? "Upgrade to a paid plan to claim your free .link domain. After the first year, renewal is $12/year."
                                : "By claiming your .link domain, you agree to our terms. After the first year, your renewal is $12/year."
                            }
                        </Text>

                        <Flex gap="md" justify="flex-end">
                            <Button
                                variant="default"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            {true && (
                                <Button
                                    loading={isPurchasing && purchasingDomainId === mainDomain.domainName}
                                    onClick={isTrialAccount(plan) ? () => window.open('/account/billing', '_blank') : handleClaimDomain}
                                    disabled={!mainDomain.purchasable || (isPurchasing && purchasingDomainId !== mainDomain.domainName)}
                                    title={isTrialAccount(plan) ? "Upgrade to a paid plan to claim this domain" : ""}
                                >
                                    {isTrialAccount(plan) ? "Upgrade Plan" : "Claim domain"}
                                </Button>
                            )}
                        </Flex>
                    </>
                )}
            </Modal>
        </>
    );
};